import { useEffect, useRef, useState, useMemo } from 'react'
import {
  Autocomplete,
  Skeleton,
  TextField,
  Checkbox,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { useTranslation } from 'react-i18next'

import { createRefinedSelectedOptions } from './Helper'
import OptionWithTooltip, {
  shouldShowTooltip,
} from 'components/Shared/OptionWithTooltip'
import CustomChip from 'components/Shared/Chip'
import { MultiSelectWithSearchType } from 'types/MultiSelectWithSearch'
import usePrevious from 'lib/hooks/UsePrevious'
import {
  DEFAULT_CHAR_LIMIT,
  zIndexLoweredFilterCSS,
  zIndexRaisedFilterCSS,
} from 'lib/GlobalConstants'

const checkboxOutlineIcon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

// To make autocomplete input filed float over parent content
const textFieldCSS = {
  width: '15.63rem', // default width
  background: 'white',
  zIndex: 1,
  position: 'absolute',
}

const MultiSelectWithSearch = (props: MultiSelectWithSearchType) => {
  const { t } = useTranslation()
  const inputRef = useRef({ focus: () => null })
  const [cssUpdates, setCssUpdates] = useState<any>(zIndexLoweredFilterCSS)

  const {
    style = {},
    charLimit = DEFAULT_CHAR_LIMIT,
    icon,
    selectedValues = [],
    options = [],
    limitTags = 1,
    optionKey = '',
    dataCyLabel = '',
    labelKey = '',
    placeholderKey = '',
    defaultOpen = false,
    handleOnChange,
    isOptionEqualToValue,
    isLoaderEnabled,
    isAutoFocusEnabled = true,
  } = props
  const prevProps = usePrevious({ options })

  const loader = useMemo(
    () =>
      isLoaderEnabled && typeof prevProps === 'undefined' && !options.length,

    [options],
  )

  const renderTags = (tagValue: object[], getTagProps: any) => {
    return tagValue.map((option: any, index: number) => {
      return (
        <CustomChip
          key={`${option[optionKey]}-${index}`}
          size="small"
          filterValues={[option[optionKey]]}
          extraProps={{
            ...getTagProps({ index }),
            color: 'primary',
          }}
        />
      )
    })
  }

  const renderOptions = (props: any, option: any, state: any) => {
    const customProps = { ...props, ...{ key: props.id } }

    return (
      <li {...customProps}>
        <Checkbox
          icon={checkboxOutlineIcon}
          checkedIcon={checkedIcon}
          checked={state.selected}
          sx={{ marginRight: '0.5rem' }}
        />
        {shouldShowTooltip(option[optionKey]) ? (
          <OptionWithTooltip option={option[optionKey]} charLimit={charLimit} />
        ) : (
          option[optionKey]
        )}
      </li>
    )
  }

  // Set icon if passed in props
  const getIconForTextField = (inputProps: any) => {
    return {
      ...inputProps,
      startAdornment: (
        <>
          <InputAdornment position="start">{icon}</InputAdornment>
          {inputProps.startAdornment}
        </>
      ),
    }
  }

  const renderInput = (params: any) => {
    const inputProps = icon
      ? getIconForTextField(params.InputProps)
      : params.InputProps
    const loaderInputProps = {
      ...(loader
        ? {
            endAdornment: (
              <>
                {
                  <CircularProgress
                    color="inherit"
                    size={20}
                    sx={{ marginRight: '2rem' }}
                  />
                }
                {params.InputProps.endAdornment}
              </>
            ),
          }
        : null),
    }
    const updateinputProps = { ...inputProps, ...loaderInputProps }
    const cssStyles = { ...textFieldCSS, ...cssUpdates }
    return (
      <TextField
        {...params}
        variant="outlined"
        size="small"
        inputRef={inputRef}
        style={{
          ...cssStyles,
          ...style,
        }}
        InputProps={updateinputProps}
        data-cy={dataCyLabel}
        data-testid={dataCyLabel}
        label={t(labelKey)}
        placeholder={t(placeholderKey)}
        onFocus={() => {
          setCssUpdates(zIndexRaisedFilterCSS)
        }}
        onBlur={() => {
          setCssUpdates(zIndexLoweredFilterCSS)
        }}
      />
    )
  }

  //Focus textfield on mount
  useEffect(() => {
    defaultOpen && inputRef.current.focus()
  }, [defaultOpen])
  let refinedSelectedOptions = selectedValues
  if (dataCyLabel == 'ethnicity' || dataCyLabel == 'gender') {
    refinedSelectedOptions = createRefinedSelectedOptions(
      options,
      selectedValues,
    )
  }

  const autoCompleteProps: any = {
    loading: loader,
    loadingText: <Skeleton height={20} />,
    multiple: true,
    id: labelKey,
    size: 'small',
    openOnFocus: isAutoFocusEnabled,
    style,
    disableCloseOnSelect: true,
    value: refinedSelectedOptions,
    limitTags: limitTags,
    options,
    onChange: (event: any, value: any) => handleOnChange(value),
    getOptionLabel: (option: any) => option[optionKey],
    isOptionEqualToValue,
    renderTags,
    renderOption: (props: any, option: any, state: any) =>
      options.length ? renderOptions(props, option, state) : null,
    renderInput,
    openText: t('filters.ariaTexts.open'),
    closeText: t('filters.ariaTexts.close'),
    clearText: t('filters.ariaTexts.clear'),
    noOptionsText: t('filters.ariaTexts.noOptions'),
  }

  return <Autocomplete {...autoCompleteProps} />
}

export default MultiSelectWithSearch
